"use client";

import * as React from "react";
import { ACTIVE_ACCOUNTS, accountLabel } from "../../data/accounts";

type Line = {
  accountNo: string; // Konto-Nr.
  memo: string;
  debit: string;
  credit: string;
};

type FormState = {
  partnerId: string;
  partnerType: "DEBITOR" | "KREDITOR" | "";
  dueDate: string;
  createOpenItem: boolean;
  accountModule: "LIQUIDITY" | "INVESTMENT";
  documentNo: string;
  documentStorageKey: string;
  date: string;
  ref: string;
  text: string;
  status: "PROVISORISCH" | "FINAL";
  vatTransactionType: "SALE" | "PURCHASE";
  vatCase: "INLAND" | "IMPORT" | "EXPORT" | "EXEMPT" | "OPTION";
  vatServiceDate: string;
  paymentDate: string;
  hasExportEvidence: boolean;
  vatRateCode: string;
  vatRatePercent: string;
  vatDeductionPercent: string;
  vatAmountType: "EXKL" | "INKL";
  vatInvoiceAmount: string;
  currencyCode: "CHF" | "EUR" | "USD" | "GBP";
  fxRateToChf: string;
  fxRateDate: string;
  fxSource: "SNB" | "ESTV" | "ECB" | "MANUAL" | "SYSTEM";
  totalForeign: string;
  lines: Line[];
};

type VatCodeOption = {
  code: string;
  label: string;
  rate: string;
  tx: "SALE" | "PURCHASE" | "BOTH";
  vatCaseDefault?: "INLAND" | "IMPORT" | "EXPORT" | "EXEMPT" | "OPTION";
  amountTypeDefault?: "EXKL" | "INKL";
  postingHint?: string | null;
  bookingTextExample?: string | null;
};

type PartnerOption = {
  id: string;
  partnerNo: string;
  partnerType: "DEBITOR" | "KREDITOR" | "BOTH";
  name: string;
  paymentTermsDays: number | null;
};

const VAT_CODE_OPTIONS: VatCodeOption[] = [
  { code: "UST81EXKL", label: "Umsatzsteuer Normalsatz exkl.", rate: "8.1", tx: "SALE", vatCaseDefault: "INLAND", amountTypeDefault: "EXKL" },
  { code: "UST81INKL", label: "Umsatzsteuer Normalsatz inkl.", rate: "8.1", tx: "SALE", vatCaseDefault: "INLAND", amountTypeDefault: "INKL" },
  { code: "UST26EXKL", label: "Umsatzsteuer reduziert exkl.", rate: "2.6", tx: "SALE", vatCaseDefault: "INLAND", amountTypeDefault: "EXKL" },
  { code: "UST26INKL", label: "Umsatzsteuer reduziert inkl.", rate: "2.6", tx: "SALE", vatCaseDefault: "INLAND", amountTypeDefault: "INKL" },
  { code: "UST38EXKL", label: "Umsatzsteuer Beherbergung exkl.", rate: "3.8", tx: "SALE", vatCaseDefault: "INLAND", amountTypeDefault: "EXKL" },
  { code: "UST38INKL", label: "Umsatzsteuer Beherbergung inkl.", rate: "3.8", tx: "SALE", vatCaseDefault: "INLAND", amountTypeDefault: "INKL" },
  { code: "EXP0", label: "Export 0%", rate: "0", tx: "SALE", vatCaseDefault: "EXPORT", amountTypeDefault: "EXKL" },
  { code: "FREI0", label: "Ausgenommen / nicht steuerbar", rate: "0", tx: "BOTH", vatCaseDefault: "EXEMPT", amountTypeDefault: "EXKL" },
  { code: "VSM81EXKL", label: "Vorsteuer Material/DL 8.1 exkl.", rate: "8.1", tx: "PURCHASE", vatCaseDefault: "INLAND", amountTypeDefault: "EXKL" },
  { code: "VSM81INKL", label: "Vorsteuer Material/DL 8.1 inkl.", rate: "8.1", tx: "PURCHASE", vatCaseDefault: "INLAND", amountTypeDefault: "INKL" },
  { code: "VSM26EXKL", label: "Vorsteuer Material/DL 2.6 exkl.", rate: "2.6", tx: "PURCHASE", vatCaseDefault: "INLAND", amountTypeDefault: "EXKL" },
  { code: "VSM26INKL", label: "Vorsteuer Material/DL 2.6 inkl.", rate: "2.6", tx: "PURCHASE", vatCaseDefault: "INLAND", amountTypeDefault: "INKL" },
  { code: "VSM38EXKL", label: "Vorsteuer Beherbergung 3.8 exkl.", rate: "3.8", tx: "PURCHASE", vatCaseDefault: "INLAND", amountTypeDefault: "EXKL", postingHint: "1172" },
  { code: "VSM38INKL", label: "Vorsteuer Beherbergung 3.8 inkl.", rate: "3.8", tx: "PURCHASE", vatCaseDefault: "INLAND", amountTypeDefault: "INKL", postingHint: "1172" },
  { code: "VSB81EXKL", label: "Vorsteuer Investitionen 8.1 exkl.", rate: "8.1", tx: "PURCHASE", vatCaseDefault: "INLAND", amountTypeDefault: "EXKL" },
  { code: "VSB81INKL", label: "Vorsteuer Investitionen 8.1 inkl.", rate: "8.1", tx: "PURCHASE", vatCaseDefault: "INLAND", amountTypeDefault: "INKL" },
  { code: "IMPMAT", label: "Import Material VSt 100%", rate: "8.1", tx: "PURCHASE", vatCaseDefault: "IMPORT", amountTypeDefault: "EXKL", postingHint: "1170" },
  { code: "IMPINV", label: "Import Investitionen VSt 100%", rate: "8.1", tx: "PURCHASE", vatCaseDefault: "IMPORT", amountTypeDefault: "EXKL", postingHint: "1171" },
  { code: "VDL81EXKL", label: "Bezugsteuer 8.1 exkl.", rate: "8.1", tx: "PURCHASE", vatCaseDefault: "IMPORT", amountTypeDefault: "EXKL" },
  { code: "VDL81INKL", label: "Bezugsteuer 8.1 inkl.", rate: "8.1", tx: "PURCHASE", vatCaseDefault: "IMPORT", amountTypeDefault: "INKL" }
];

function parseMoney(v: string): number {
  const s = (v || "")
    .toString()
    .replace(/[^0-9,.\-]/g, "")
    .replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function round2(n: number) {
  return Math.round(n * 100) / 100;
}

export default function NewJournalEntryModal({
  open,
  onClose,
  onSaved
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = React.useState<FormState>({
    partnerId: "",
    partnerType: "KREDITOR",
    dueDate: "",
    createOpenItem: true,
    accountModule: "LIQUIDITY",
    documentNo: "",
    documentStorageKey: "",
    date: new Date().toISOString().slice(0, 10),
    ref: "",
    text: "",
    status: "PROVISORISCH",
    vatTransactionType: "PURCHASE",
    vatCase: "INLAND",
    vatServiceDate: new Date().toISOString().slice(0, 10),
    paymentDate: "",
    hasExportEvidence: false,
    vatRateCode: "VSM81EXKL",
    vatRatePercent: "8.1",
    vatDeductionPercent: "100",
    vatAmountType: "EXKL",
    vatInvoiceAmount: "",
    currencyCode: "CHF",
    fxRateToChf: "1",
    fxRateDate: new Date().toISOString().slice(0, 10),
    fxSource: "SYSTEM",
    totalForeign: "",
    lines: [
      { accountNo: "", memo: "", debit: "", credit: "" },
      { accountNo: "", memo: "", debit: "", credit: "" },
    ],
  });
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [vatCodeOptions, setVatCodeOptions] = React.useState<VatCodeOption[]>(VAT_CODE_OPTIONS);
  const [partnerOptions, setPartnerOptions] = React.useState<PartnerOption[]>([]);

  React.useEffect(() => {
    if (!open) return;
    setForm({
      partnerId: "",
      partnerType: "KREDITOR",
      dueDate: "",
      createOpenItem: true,
      accountModule: "LIQUIDITY",
      documentNo: "",
      documentStorageKey: "",
      date: new Date().toISOString().slice(0, 10),
      ref: "",
      text: "",
      status: "PROVISORISCH",
      vatTransactionType: "PURCHASE",
      vatCase: "INLAND",
      vatServiceDate: new Date().toISOString().slice(0, 10),
      paymentDate: "",
      hasExportEvidence: false,
      vatRateCode: "VSM81EXKL",
      vatRatePercent: "8.1",
      vatDeductionPercent: "100",
      vatAmountType: "EXKL",
      vatInvoiceAmount: "",
      currencyCode: "CHF",
      fxRateToChf: "1",
      fxRateDate: new Date().toISOString().slice(0, 10),
      fxSource: "SYSTEM",
      totalForeign: "",
      lines: [
        { accountNo: "", memo: "", debit: "", credit: "" },
        { accountNo: "", memo: "", debit: "", credit: "" },
      ],
    });
    setSelectedFile(null);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    fetch("/api/partners?activeOnly=true", { cache: "no-store" })
      .then((r) => r.json())
      .then((data: { ok?: boolean; items?: Array<{ id: string; partnerNo: string; partnerType: "DEBITOR" | "KREDITOR" | "BOTH"; name: string; paymentTermsDays: number | null }> }) => {
        if (!data.ok || !Array.isArray(data.items)) return;
        setPartnerOptions(
          data.items.map((x) => ({
            id: x.id,
            partnerNo: x.partnerNo,
            partnerType: x.partnerType,
            name: x.name,
            paymentTermsDays: x.paymentTermsDays ?? null
          }))
        );
      })
      .catch(() => {
        setPartnerOptions([]);
      });
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    fetch("/api/vat/codes", { cache: "no-store" })
      .then((r) => r.json())
      .then((data: { ok?: boolean; items?: Array<{ code: string; label: string; ratePercent: number; txType: "SALE" | "PURCHASE" | "BOTH"; vatCaseDefault: FormState["vatCase"]; amountTypeDefault: FormState["vatAmountType"]; postingHint?: string | null; bookingTextExample?: string | null }> }) => {
        if (!data.ok || !Array.isArray(data.items) || data.items.length === 0) return;
        setVatCodeOptions(
          data.items.map((x) => ({
            code: x.code,
            label: x.label,
            rate: String(x.ratePercent),
            tx: x.txType,
            vatCaseDefault: x.vatCaseDefault,
            amountTypeDefault: x.amountTypeDefault,
            postingHint: x.postingHint || null,
            bookingTextExample: x.bookingTextExample || null
          }))
        );
      })
      .catch(() => {
        setVatCodeOptions(VAT_CODE_OPTIONS);
      });
  }, [open]);

  React.useEffect(() => {
    const byTx = vatCodeOptions.filter((o) => o.tx === "BOTH" || o.tx === form.vatTransactionType);
    const current = byTx.find((o) => o.code === form.vatRateCode);
    if (!current && byTx.length > 0) {
      setForm((p) => ({
        ...p,
        vatRateCode: byTx[0].code,
        vatRatePercent: byTx[0].rate
      }));
    }
  }, [form.vatTransactionType, form.vatRateCode, vatCodeOptions]);

  React.useEffect(() => {
    if (!open) return;
    if (!form.vatRateCode || !form.vatServiceDate) return;
    const controller = new AbortController();
    const url = `/api/vat/rates/resolve?code=${encodeURIComponent(form.vatRateCode)}&serviceDate=${encodeURIComponent(form.vatServiceDate)}&fallbackRatePercent=${encodeURIComponent(form.vatRatePercent || "")}`;
    fetch(url, { cache: "no-store", signal: controller.signal })
      .then((r) => r.json())
      .then((data: { ok?: boolean; ratePercent?: number }) => {
        if (!data.ok || typeof data.ratePercent !== "number") return;
        setForm((p) => ({ ...p, vatRatePercent: String(data.ratePercent) }));
      })
      .catch(() => {});
    return () => controller.abort();
  }, [open, form.vatRateCode, form.vatServiceDate]);

  React.useEffect(() => {
    if (form.vatCase !== "IMPORT") return;
    const importPreferred = vatCodeOptions.find((o) => o.code === "IMPMAT") || vatCodeOptions.find((o) => o.vatCaseDefault === "IMPORT");
    if (!importPreferred) return;
    if (form.vatRateCode !== importPreferred.code) {
      setForm((p) => ({
        ...p,
        vatRateCode: importPreferred.code,
        vatRatePercent: importPreferred.rate,
        vatAmountType: "EXKL"
      }));
    }
  }, [form.vatCase, form.vatRateCode, vatCodeOptions]);

  React.useEffect(() => {
    setForm((p) => {
      const wanted = p.vatTransactionType === "SALE" ? "DEBITOR" : "KREDITOR";
      return p.partnerType === wanted ? p : { ...p, partnerType: wanted };
    });
  }, [form.vatTransactionType]);

  const totals = React.useMemo(() => {
    const debit = round2(form.lines.reduce((sum, l) => sum + parseMoney(l.debit), 0));
    const credit = round2(form.lines.reduce((sum, l) => sum + parseMoney(l.credit), 0));
    const diff = round2(debit - credit);
    return { debit, credit, diff };
  }, [form.lines]);

  const vatAmountPreview = React.useMemo(() => {
    const rate = parseMoney(form.vatRatePercent);
    const amount = parseMoney(form.vatInvoiceAmount);
    if (!(rate > 0) || !(amount > 0)) return null;
    if (form.vatAmountType === "EXKL") {
      const net = round2(amount);
      const tax = round2(net * (rate / 100));
      const gross = round2(net + tax);
      return { net, tax, gross };
    }
    const gross = round2(amount);
    const net = round2(gross / (1 + rate / 100));
    const tax = round2(gross - net);
    return { net, tax, gross };
  }, [form.vatRatePercent, form.vatInvoiceAmount, form.vatAmountType]);

  const isBalanced =
    Math.abs(totals.diff) < 0.01 && (totals.debit + totals.credit) > 0;

  const lineValidation = React.useMemo(() => {
    // Zeile zählt nur, wenn debit/credit > 0 oder memo/account gesetzt
    return form.lines.map((l) => {
      const d = parseMoney(l.debit);
      const c = parseMoney(l.credit);
      const hasAmount = d > 0 || c > 0;

      const both = d > 0 && c > 0;
      const missingAccount = hasAmount && !l.accountNo;

      return {
        hasAmount,
        both,
        missingAccount,
      };
    });
  }, [form.lines]);

  const hasErrors = lineValidation.some(v => v.both || v.missingAccount);
  const hasDocumentMeta = form.documentNo.trim().length > 0;
  const hasDocumentFileRef = form.documentStorageKey.trim().length > 0;
  const canSave = isBalanced && !hasErrors && hasDocumentMeta && hasDocumentFileRef;
  const partnerCandidates = React.useMemo(
    () =>
      partnerOptions.filter((p) =>
        form.partnerType === "DEBITOR"
          ? p.partnerType === "DEBITOR" || p.partnerType === "BOTH"
          : p.partnerType === "KREDITOR" || p.partnerType === "BOTH"
      ),
    [partnerOptions, form.partnerType]
  );

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((p) => ({ ...p, [key]: value }));
  }

  function updateLine(idx: number, patch: Partial<Line>) {
    setForm((p) => {
      const lines = p.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l));
      return { ...p, lines };
    });
  }

  function addLine() {
    setForm((p) => ({
      ...p,
      lines: [...p.lines, { accountNo: "", memo: "", debit: "", credit: "" }],
    }));
  }

  function removeLine(idx: number) {
    setForm((p) => {
      if (p.lines.length <= 2) return p;
      const lines = p.lines.filter((_, i) => i !== idx);
      return { ...p, lines };
    });
  }

  function autoBalance() {
    setForm((p) => {
      const debit = round2(p.lines.reduce((sum, l) => sum + parseMoney(l.debit), 0));
      const credit = round2(p.lines.reduce((sum, l) => sum + parseMoney(l.credit), 0));
      const diff = round2(debit - credit);
      if (Math.abs(diff) < 0.01) return p;

      const lastIdx = p.lines.length - 1;
      const patch: Partial<Line> =
        diff > 0
          ? { credit: String(Math.abs(diff)), debit: "" } // mehr Haben nötig
          : { debit: String(Math.abs(diff)), credit: "" }; // mehr Soll nötig

      const lines = p.lines.map((l, i) => (i === lastIdx ? { ...l, ...patch } : l));
      return { ...p, lines };
    });
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave) return;
    setError(null);
    setSaving(true);

    const payload = {
      partnerId: form.partnerId || undefined,
      partnerType: form.partnerId ? (form.partnerType || undefined) : undefined,
      dueDate: form.partnerId ? (form.dueDate || undefined) : undefined,
      createOpenItem: form.partnerId ? form.createOpenItem : undefined,
      accountModule: form.accountModule,
      documentNo: form.documentNo,
      documentTitle: form.text,
      documentStorageKey: form.documentStorageKey,
      date: form.date,
      ref: form.ref,
      text: form.text,
      status: form.status,
      lines: form.lines.map((l) => ({
        accountNo: l.accountNo,
        memo: l.memo,
        debit: parseMoney(l.debit),
        credit: parseMoney(l.credit),
      })),
      vat: {
        transactionType: form.vatTransactionType,
        vatCase: form.vatCase,
        serviceDate: form.vatServiceDate || undefined,
        paymentDate: form.paymentDate || undefined,
        hasExportEvidence: form.hasExportEvidence,
        vatRateCode: form.vatRateCode || undefined,
        vatRatePercent: form.vatRatePercent ? parseMoney(form.vatRatePercent) : undefined,
        deductionPercent: form.vatDeductionPercent ? parseMoney(form.vatDeductionPercent) : undefined,
        taxableBaseChf: form.vatCase === "IMPORT" && form.vatInvoiceAmount ? parseMoney(form.vatInvoiceAmount) : undefined,
        amountType: form.vatCase === "IMPORT" ? undefined : form.vatAmountType,
        invoiceAmount: form.vatCase === "IMPORT" ? undefined : (form.vatInvoiceAmount ? parseMoney(form.vatInvoiceAmount) : undefined),
        netAmount: form.vatCase === "IMPORT" ? undefined : vatAmountPreview?.net,
        taxAmount: form.vatCase === "IMPORT" ? undefined : vatAmountPreview?.tax,
        grossAmount: form.vatCase === "IMPORT" ? undefined : vatAmountPreview?.gross,
        currencyCode: form.currencyCode,
        fxRateToChf: form.fxRateToChf ? parseMoney(form.fxRateToChf) : undefined,
        fxRateDate: form.fxRateDate || undefined,
        fxSource: form.fxSource,
        totalForeign: form.totalForeign ? parseMoney(form.totalForeign) : undefined
      }
    };

    fetch("/api/journal", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data?.error || "Speichern fehlgeschlagen");
        }
        onSaved();
        onClose();
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : "Speichern fehlgeschlagen";
        setError(msg);
      })
      .finally(() => {
        setSaving(false);
      });
  }

  async function uploadDocumentFile() {
    if (!selectedFile) return;
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("documentNo", form.documentNo);
      fd.append("documentTitle", form.text);
      fd.append("file", selectedFile);

      const res = await fetch("/api/documents/upload", {
        method: "POST",
        body: fd
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.error || "Upload fehlgeschlagen");
      }
      if (payload.documentNo) {
        set("documentNo", payload.documentNo);
      }
      if (payload.storageKey) {
        set("documentStorageKey", payload.storageKey);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  }

  if (!open) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="modal">
        <div className="modal-head">
          <div>
            <div className="modal-title">Neue Buchung</div>
            <div className="modal-sub">
              Mehrzeilig • Konto als Dropdown • Soll/Haben muss gleich sein
            </div>
          </div>

          <button className="btn btn-ghost" type="button" onClick={onClose} title="Schliessen">
            ✕
          </button>
        </div>

        <form onSubmit={submit} className="modal-body">
          <label className="field">
            <span>Beleg-Nr.</span>
            <input
              className="input"
              placeholder="z.B. BELEG-2026-0001"
              value={form.documentNo}
              onChange={(e) => set("documentNo", e.target.value)}
            />
          </label>

          <label className="field">
            <span>Beleg-Datei Key</span>
            <input
              className="input"
              placeholder="z.B. docs/demo-kmu/2026/BELEG-2026-0001.pdf"
              value={form.documentStorageKey}
              onChange={(e) => set("documentStorageKey", e.target.value)}
            />
          </label>

          <div className="field">
            <span>Belegdatei Upload</span>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input
                className="input"
                type="file"
                accept="image/*,application/pdf"
                capture="environment"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              />
              <button
                className="btn"
                type="button"
                onClick={uploadDocumentFile}
                disabled={!selectedFile || uploading}
              >
                {uploading ? "Upload..." : "Datei hochladen"}
              </button>
            </div>
          </div>

          <div className="grid-2">
            <label className="field">
              <span>Datum</span>
              <input
                className="input"
                type="date"
                value={form.date}
                onChange={(e) => set("date", e.target.value)}
              />
            </label>

            <label className="field">
              <span>Referenz</span>
              <input
                className="input"
                placeholder="z.B. KR-10023"
                value={form.ref}
                onChange={(e) => set("ref", e.target.value)}
              />
            </label>
          </div>

          <label className="field">
            <span>Buchungstext</span>
            <input
              className="input"
              placeholder="z.B. Swisscom Rechnung Januar"
              value={form.text}
              onChange={(e) => set("text", e.target.value)}
            />
          </label>

          <div className="grid-2">
            <label className="field">
              <span>Partner-Typ</span>
              <select
                className="select"
                value={form.partnerType}
                onChange={(e) => {
                  const t = e.target.value as FormState["partnerType"];
                  set("partnerType", t);
                  set("partnerId", "");
                }}
              >
                <option value="DEBITOR">Debitor</option>
                <option value="KREDITOR">Kreditor</option>
              </select>
            </label>
            <label className="field">
              <span>Partner (optional)</span>
              <select
                className="select"
                value={form.partnerId}
                onChange={(e) => {
                  const id = e.target.value;
                  set("partnerId", id);
                  const partner = partnerCandidates.find((p) => p.id === id);
                  if (partner && !form.dueDate && typeof partner.paymentTermsDays === "number") {
                    const d = new Date(form.date);
                    d.setDate(d.getDate() + partner.paymentTermsDays);
                    set("dueDate", d.toISOString().slice(0, 10));
                  }
                }}
              >
                <option value="">— kein Partner —</option>
                {partnerCandidates.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.partnerNo} · {p.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid-2">
            <label className="field">
              <span>Fälligkeitsdatum (für OP)</span>
              <input
                className="input"
                type="date"
                value={form.dueDate}
                onChange={(e) => set("dueDate", e.target.value)}
                disabled={!form.partnerId}
              />
            </label>
            <label className="field">
              <span>Offenen Posten erstellen</span>
              <select
                className="select"
                value={form.createOpenItem ? "yes" : "no"}
                onChange={(e) => set("createOpenItem", e.target.value === "yes")}
                disabled={!form.partnerId}
              >
                <option value="yes">Ja</option>
                <option value="no">Nein</option>
              </select>
            </label>
          </div>

          <div className="grid-2">
            <label className="field">
              <span>Konten-Modul</span>
              <select
                className="select"
                value={form.accountModule}
                onChange={(e) => set("accountModule", e.target.value as FormState["accountModule"])}
              >
                <option value="LIQUIDITY">LIQUIDITY (inkl. 106x Wertschriften kurzfristig)</option>
                <option value="INVESTMENT">INVESTMENT (14xx Anlagevermögen)</option>
              </select>
            </label>

            <label className="field">
              <span>Status</span>
              <select
                className="select"
                value={form.status}
                onChange={(e) => set("status", e.target.value as FormState["status"])}
              >
                <option value="PROVISORISCH">PROVISORISCH</option>
                <option value="FINAL">FINAL</option>
              </select>
            </label>

            <div className="field">
              <span>Totals</span>
              <div style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                alignItems: "center",
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid var(--border)",
                background: "rgba(0,0,0,0.02)"
              }}>
                <span><strong>Soll:</strong> {totals.debit.toFixed(2)}</span>
                <span><strong>Haben:</strong> {totals.credit.toFixed(2)}</span>
                <span>
                  <strong>Diff:</strong>{" "}
                  <span className={isBalanced ? "pill pill-ok" : "pill pill-warn"}>
                    {totals.diff.toFixed(2)}
                  </span>
                </span>
              </div>
            </div>
          </div>

          <div className="grid-2">
            <label className="field">
              <span>MWST Transaktion</span>
              <select
                className="select"
                value={form.vatTransactionType}
                onChange={(e) => set("vatTransactionType", e.target.value as FormState["vatTransactionType"])}
              >
                <option value="PURCHASE">PURCHASE (Einkauf)</option>
                <option value="SALE">SALE (Verkauf)</option>
              </select>
            </label>
            <label className="field">
              <span>MWST Fall</span>
              <select
                className="select"
                value={form.vatCase}
                onChange={(e) => set("vatCase", e.target.value as FormState["vatCase"])}
              >
                <option value="INLAND">INLAND</option>
                <option value="IMPORT">IMPORT</option>
                <option value="EXPORT">EXPORT</option>
                <option value="EXEMPT">EXEMPT</option>
                <option value="OPTION">OPTION</option>
              </select>
            </label>
          </div>

          <div className="grid-2">
            <label className="field">
              <span>Leistungsdatum (für Satz-Gültigkeit)</span>
              <input
                className="input"
                type="date"
                value={form.vatServiceDate}
                onChange={(e) => set("vatServiceDate", e.target.value)}
              />
            </label>
            <label className="field">
              <span>Abzugsquote %</span>
              <input
                className="input"
                placeholder="100 / 50 / 0"
                value={form.vatDeductionPercent}
                onChange={(e) => set("vatDeductionPercent", e.target.value)}
                disabled={form.vatCase !== "IMPORT"}
              />
            </label>
          </div>

          <div className="grid-2">
            <label className="field">
              <span>Zahlungsdatum (für vereinnahmt)</span>
              <input
                className="input"
                type="date"
                value={form.paymentDate}
                onChange={(e) => set("paymentDate", e.target.value)}
              />
            </label>
            <label className="field">
              <span>Exportnachweis vorhanden</span>
              <select
                className="select"
                value={form.hasExportEvidence ? "yes" : "no"}
                onChange={(e) => set("hasExportEvidence", e.target.value === "yes")}
              >
                <option value="no">Nein</option>
                <option value="yes">Ja</option>
              </select>
            </label>
          </div>

          <div className="grid-2">
            <label className="field">
              <span>MWST-Code</span>
              <select
                className="select"
                value={form.vatRateCode}
                onChange={(e) => {
                  const code = e.target.value;
                  const option = vatCodeOptions.find((o) => o.code === code);
                  set("vatRateCode", code);
                  if (option) {
                    set("vatRatePercent", option.rate);
                    if (option.vatCaseDefault) set("vatCase", option.vatCaseDefault);
                    if (option.amountTypeDefault) set("vatAmountType", option.amountTypeDefault);
                  }
                }}
              >
                {vatCodeOptions.filter((o) => o.tx === "BOTH" || o.tx === form.vatTransactionType).map((o) => (
                  <option key={o.code} value={o.code}>
                    {o.code} · {o.label}{o.postingHint ? ` · Konto ${o.postingHint}` : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>MWST Satz %</span>
              <input
                className="input"
                placeholder="z.B. 8.1 / 2.6 / 3.8"
                value={form.vatRatePercent}
                onChange={(e) => set("vatRatePercent", e.target.value)}
              />
            </label>
          </div>

          <div className="grid-2">
            <label className="field">
              <span>Rechnungsbetrag-Typ</span>
              <select
                className="select"
                value={form.vatAmountType}
                onChange={(e) => set("vatAmountType", e.target.value as FormState["vatAmountType"])}
                disabled={form.vatCase === "IMPORT"}
              >
                <option value="EXKL">exkl. MWST</option>
                <option value="INKL">inkl. MWST</option>
              </select>
            </label>
            <label className="field">
              <span>{form.vatCase === "IMPORT" ? "Bemessungsbetrag CHF" : `Rechnungsbetrag (${form.vatAmountType === "EXKL" ? "Netto" : "Brutto"})`}</span>
              <input
                className="input"
                placeholder={form.vatCase === "IMPORT" ? "z.B. 5500.00" : "z.B. 1081.00"}
                value={form.vatInvoiceAmount}
                onChange={(e) => set("vatInvoiceAmount", e.target.value)}
              />
            </label>
          </div>
          {form.vatCase === "IMPORT" && (
            <div style={{ marginTop: -4, marginBottom: 8, padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 10, fontSize: 12, color: "var(--muted)" }}>
              Import: kein inkl/exkl-Splitting. MWST-Betrag wird direkt auf 1170 (Material) oder 1171 (Investition) gebucht.
            </div>
          )}
          {vatAmountPreview && (
            <div style={{ marginTop: -4, marginBottom: 8, padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 10, fontSize: 12, color: "var(--muted)" }}>
              Netto: <strong>{vatAmountPreview.net.toFixed(2)}</strong> · MWST: <strong>{vatAmountPreview.tax.toFixed(2)}</strong> · Brutto: <strong>{vatAmountPreview.gross.toFixed(2)}</strong>
            </div>
          )}

          <div className="grid-2">
            <label className="field">
              <span>Währung (MWST-Abrechnung immer CHF)</span>
              <select
                className="select"
                value={form.currencyCode}
                onChange={(e) => {
                  const c = e.target.value as FormState["currencyCode"];
                  set("currencyCode", c);
                  if (c === "CHF") {
                    set("fxRateToChf", "1");
                    set("fxSource", "SYSTEM");
                  } else if (form.fxRateToChf === "1") {
                    set("fxRateToChf", "");
                    set("fxSource", "MANUAL");
                  }
                }}
              >
                <option value="CHF">CHF</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
              </select>
            </label>
            <label className="field">
              <span>Kurs zu CHF</span>
              <input
                className="input"
                placeholder="z.B. 0.96"
                value={form.fxRateToChf}
                onChange={(e) => set("fxRateToChf", e.target.value)}
              />
            </label>
          </div>

          <div className="grid-2">
            <label className="field">
              <span>Kursdatum</span>
              <input
                className="input"
                type="date"
                value={form.fxRateDate}
                onChange={(e) => set("fxRateDate", e.target.value)}
              />
            </label>
            <label className="field">
              <span>Kursquelle</span>
              <select
                className="select"
                value={form.fxSource}
                onChange={(e) => set("fxSource", e.target.value as FormState["fxSource"])}
              >
                <option value="SYSTEM">SYSTEM</option>
                <option value="SNB">SNB</option>
                <option value="ESTV">ESTV</option>
                <option value="ECB">ECB</option>
                <option value="MANUAL">MANUAL</option>
              </select>
            </label>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: -6 }}>
            <button
              className="btn"
              type="button"
              onClick={async () => {
                try {
                  if (form.currencyCode === "CHF") {
                    set("fxRateToChf", "1");
                    set("fxSource", "SYSTEM");
                    return;
                  }
                  const res = await fetch(
                    `/api/fx/rates?currencyCode=${encodeURIComponent(form.currencyCode)}&rateDate=${encodeURIComponent(
                      form.fxRateDate || form.date
                    )}&sources=SNB,ESTV,ECB,MANUAL`,
                    { cache: "no-store" }
                  );
                  const data = (await res.json().catch(() => ({}))) as {
                    ok?: boolean;
                    error?: string;
                    rate?: { source: "SNB" | "ESTV" | "ECB" | "MANUAL"; rateToChf: number };
                  };
                  if (!res.ok || !data.ok || !data.rate) {
                    throw new Error(data.error || "Kein FX-Kurs gefunden");
                  }
                  set("fxRateToChf", String(data.rate.rateToChf));
                  set("fxSource", data.rate.source);
                  setError(null);
                } catch (err) {
                  setError(err instanceof Error ? err.message : "FX-Kurs konnte nicht geladen werden");
                }
              }}
            >
              Kurs laden (SNB &gt; ESTV &gt; ECB &gt; MANUAL)
            </button>
            <span style={{ color: "var(--muted)", fontSize: 12 }}>
              Verwendeter Kurs wird mit Quelle und Datum in der Buchung fix gespeichert.
            </span>
          </div>

          <label className="field">
            <span>Total in Fremdwährung</span>
            <input
              className="input"
              placeholder="z.B. 1000.00 (bei CHF optional)"
              value={form.totalForeign}
              onChange={(e) => set("totalForeign", e.target.value)}
            />
          </label>

          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 6, marginBottom: 10 }}>
            <div style={{ color: "var(--muted)", fontSize: 13 }}>
              Regel: Pro Zeile entweder <strong>Soll</strong> oder <strong>Haben</strong>.
              Wenn Betrag gesetzt ist, muss auch ein <strong>Konto</strong> gewählt sein.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn" type="button" onClick={addLine}>+ Zeile</button>
              <button className="btn btn-primary" type="button" onClick={autoBalance}>Auto-Ausgleich</button>
            </div>
          </div>

          {/* Lines table */}
          <div style={{
            border: "1px solid var(--border)",
            borderRadius: 12,
            overflow: "hidden",
            background: "var(--panel)"
          }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: "1.4fr 1.2fr 0.6fr 0.6fr 60px",
              gap: 0,
              padding: "10px 12px",
              borderBottom: "1px solid var(--border)",
              fontWeight: 800
            }}>
              <div>Konto</div>
              <div>Memo</div>
              <div>Soll</div>
              <div>Haben</div>
              <div></div>
            </div>

            {form.lines.map((l, idx) => {
              const v = lineValidation[idx];
              const rowWarn = v?.both || v?.missingAccount;

              return (
                <div
                  key={idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.4fr 1.2fr 0.6fr 0.6fr 60px",
                    gap: 10,
                    alignItems: "center",
                    padding: "10px 12px",
                    borderBottom: idx === form.lines.length - 1 ? "none" : "1px solid var(--border)",
                    background: rowWarn ? "rgba(245,158,11,0.08)" : "transparent"
                  }}
                >
                  <select
                    className="select"
                    value={l.accountNo}
                    onChange={(e) => updateLine(idx, { accountNo: e.target.value })}
                    title={v?.missingAccount ? "Bitte Konto wählen" : ""}
                  >
                    <option value="">— Konto wählen —</option>
                    {ACTIVE_ACCOUNTS.map(a => (
                      <option key={a.number} value={a.number}>
                        {accountLabel(a)}
                      </option>
                    ))}
                  </select>

                  <input
                    className="input"
                    placeholder="optional"
                    value={l.memo}
                    onChange={(e) => updateLine(idx, { memo: e.target.value })}
                  />

                  <input
                    className="input"
                    placeholder="0.00"
                    value={l.debit}
                    onChange={(e) => {
                      const val = e.target.value;
                      updateLine(idx, { debit: val, credit: parseMoney(val) > 0 ? "" : l.credit });
                    }}
                  />

                  <input
                    className="input"
                    placeholder="0.00"
                    value={l.credit}
                    onChange={(e) => {
                      const val = e.target.value;
                      updateLine(idx, { credit: val, debit: parseMoney(val) > 0 ? "" : l.debit });
                    }}
                  />

                  <button className="btn btn-ghost" type="button" title="Zeile löschen" onClick={() => removeLine(idx)}>
                    🗑️
                  </button>
                </div>
              );
            })}
          </div>

          <div className="btn-row">
            <button className="btn btn-secondary" type="button" onClick={onClose}>
              Abbrechen
            </button>
            <button
              className="btn btn-primary"
              type="submit"
              disabled={!canSave || saving}
              title={!canSave ? "Diff muss 0.00 sein, Konten und Beleg (Nr. + Datei-Key) müssen gesetzt sein" : ""}
            >
              {saving ? "Speichert..." : "Speichern"}
            </button>
          </div>

          {hasErrors && (
            <div style={{ marginTop: 10, color: "var(--muted)", fontSize: 13 }}>
              Hinweis: <strong>Keine Zeile darf Soll und Haben gleichzeitig</strong> haben. Und wenn ein Betrag gesetzt ist, muss ein <strong>Konto gewählt</strong> sein.
            </div>
          )}
          {error && (
            <div style={{ marginTop: 10, color: "#b91c1c", fontSize: 13 }}>
              {error}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
